'use client'

import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ComposedChart, Cell,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'

// ─── Types ───────────────────────────────────────────────────────────────────

interface WeeklyPoint { week: number; avg_score: number }
interface RoleAvg { role: string; avg_score: number }
interface TeamMember {
  user_id: string
  display_name: string
  role: string
  avg_score: number
  call_count: number
  fatal_count: number
}
interface ParetoPoint {
  name: string
  fatal_count: number
  cumulative_pct: number
  avg_score: number
}
interface ScoreBin { range: string; count: number }
interface FilterOption {
  code: string
  label: string
  employee_count: number
  call_count: number
}
interface KpiFilterContext {
  primary_filter: 'product' | 'site' | 'both'
  show_site_filter: boolean
  show_product_filter: boolean
  site_label: string
  product_label: string
}
interface ProductOutcomes { good: number; bad: number; neutral: number }
interface GradeBreakdown { A: number; B: number; C: number; D: number }

interface DirectReport {
  display_name: string
  role: string
  avg_score: number
  call_count: number
  fatal_count: number
}

interface KpiData {
  my_score: number
  my_role: string
  call_count: number
  fatal_count: number
  weekly_trend: WeeklyPoint[]
  role_averages: RoleAvg[]
  team_members: TeamMember[]
  pareto_data: ParetoPoint[]
  score_distribution?: ScoreBin[]
  available_products?: FilterOption[]
  available_sites?: FilterOption[]
  filter_context?: KpiFilterContext
  selected_product?: string | null
  selected_site?: string | null
  drilldown_label?: string | null
  product_outcomes?: ProductOutcomes
  grade_breakdown?: GradeBreakdown
  employee_count?: number
  team_size?: number
  direct_reports?: DirectReport[]
}

interface KpiViewProps {
  startDate?: string
  endDate?: string
  targetEmployeeId?: number
}

interface PolicyParetoPoint {
  name: string
  fatal_count: number
  total_count: number
  cumulative_pct: number
}

interface PolicyParetoData {
  pareto_data: PolicyParetoPoint[]
  total_fatal_violations: number
  total_calls_analyzed: number
  ai_analysis: string | null
}

// ─── Colors & Helpers ─────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  telecaller:      '#6366f1',
  supervisor:      '#3b82f6',
  manager:         '#10b981',
  process_head:    '#14b8a6',
  hod:             '#a855f7',
  quality_manager: '#f59e0b',
  quality_admin:   '#ec4899',
}

const ROLE_LABELS: Record<string, string> = {
  telecaller:      'Telecaller',
  supervisor:      'Supervisor',
  manager:         'Manager',
  process_head:    'Process Head',
  hod:             'HOD',
  quality_manager: 'Quality Mgr',
  quality_admin:   'Quality Admin',
}

const CHART_GRID = 'rgba(15, 23, 42, 0.08)'
const CHART_TICK = '#64748b'

const SCORE_BIN_COLOR = (range: string) => {
  const low = parseInt(range)
  if (low >= 85) return '#10b981'
  if (low >= 70) return '#3b82f6'
  if (low >= 55) return '#f59e0b'
  return '#ef4444'
}

function gradeFromScore(s: number) {
  if (s >= 85) return { label: 'A', color: 'text-emerald-400', bg: 'bg-emerald-500/20' }
  if (s >= 70) return { label: 'B', color: 'text-blue-400', bg: 'bg-blue-500/20' }
  if (s >= 55) return { label: 'C', color: 'text-amber-400', bg: 'bg-amber-500/20' }
  return { label: 'D', color: 'text-red-400', bg: 'bg-red-500/20' }
}

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.4 } }),
}

// ─── Tooltip helpers ──────────────────────────────────────────────────────────

// recharts passes these props to custom content components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface RCTooltipProps { active?: boolean; payload?: readonly any[]; label?: string }

function ScoreTooltip({ active, payload, label }: RCTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className='rounded-lg border border-white/10 bg-brand-black/90 p-3 text-xs shadow-xl backdrop-blur'>
      <p className='mb-1 font-medium text-white'>{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: '#a5b4fc' }}>{p.name}: <span className='font-semibold text-white'>{p.value}%</span></p>
      ))}
    </div>
  )
}

function DistributionTooltip({ active, payload, label }: RCTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className='rounded-lg border border-white/10 bg-brand-black/90 p-3 text-xs shadow-xl backdrop-blur'>
      <p className='font-medium text-white'>Score {label}</p>
      <p className='text-brand-muted'>Members: <span className='text-white font-semibold'>{String(payload[0]?.value)}</span></p>
    </div>
  )
}

function ParetoTooltip({ active, payload, label }: RCTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className='rounded-lg border border-white/10 bg-brand-black/90 p-3 text-xs shadow-xl backdrop-blur max-w-[200px]'>
      <p className='mb-1 font-medium text-white truncate'>{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: <span className='font-semibold text-white'>{p.value}{p.name === 'Cumulative %' ? '%' : ''}</span></p>
      ))}
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiStatCard({ title, value, suffix = '', icon: Icon, colorClass, delay, sub }:
  { title: string; value: number | string; suffix?: string; icon: React.ElementType; colorClass: string; delay: number; sub?: string }) {
  return (
    <motion.div custom={delay} variants={cardVariants} initial='hidden' animate='visible'>
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={2} />
        <CardContent className='relative z-10 p-5'>
          <div className='flex items-start justify-between'>
            <div>
              <p className='text-xs font-medium text-muted-foreground'>{title}</p>
              <p className='mt-1.5 text-3xl font-bold tracking-tight text-brand-navy'>
                {value}{suffix}
              </p>
              {sub && <p className='mt-1 text-[11px] text-muted-foreground'>{sub}</p>}
            </div>
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', colorClass)}>
              <Icon className='h-5 w-5 text-white' strokeWidth={1.5} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className='flex h-40 flex-col items-center justify-center gap-2 text-brand-muted'>
      <Icons.barChart className='h-8 w-8 opacity-30' />
      <p className='text-sm'>{label}</p>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ALL_PRODUCTS = '__all__'
const ALL_SITES = '__all__'

export function KpiView({ startDate, endDate, targetEmployeeId }: KpiViewProps) {
  const [data, setData] = useState<KpiData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState(ALL_PRODUCTS)
  const [selectedSite, setSelectedSite] = useState(ALL_SITES)
  const [policyPareto, setPolicyPareto] = useState<PolicyParetoData | null>(null)
  const [paretoLoading, setParetoLoading] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('start_date', startDate)
      if (endDate) params.set('end_date', endDate)
      if (selectedProduct && selectedProduct !== ALL_PRODUCTS) {
        params.set('product', selectedProduct)
      }
      if (selectedSite && selectedSite !== ALL_SITES) {
        params.set('site', selectedSite)
      }
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      const res = await apiClient.get(`/api/hierarchy/kpi?${params}`) as KpiData
      setData(res)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load KPI data')
    } finally {
      setIsLoading(false)
    }

    // Fetch parameter-based pareto in parallel (non-blocking)
    try {
      setParetoLoading(true)
      const pp = new URLSearchParams()
      if (startDate) pp.set('start_date', startDate)
      if (endDate) pp.set('end_date', endDate)
      if (targetEmployeeId) pp.set('target_employee_id', String(targetEmployeeId))
      const paretoRes = await apiClient.get<PolicyParetoData>(`/api/analysis/pareto-parameters?${pp}`)
      setPolicyPareto(paretoRes)
    } catch (paretoErr) {
      console.error('[Pareto] failed to load:', paretoErr)
    } finally {
      setParetoLoading(false)
    }
  }, [startDate, endDate, selectedProduct, selectedSite, targetEmployeeId])

  useEffect(() => { load() }, [load])

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
          {[...Array(4)].map((_, i) => <Skeleton key={i} className='h-28 rounded-2xl' />)}
        </div>
        <div className='grid gap-6 lg:grid-cols-2'>
          {[...Array(4)].map((_, i) => <Skeleton key={i} className='h-72 rounded-2xl' />)}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className='flex h-40 items-center justify-center'>
          <p className='text-sm text-red-400'>{error}</p>
        </CardContent>
      </Card>
    )
  }

  if (!data) return null

  const filterCtx = data.filter_context
  const showSite = filterCtx?.show_site_filter ?? (data.available_sites?.length ?? 0) > 0
  const showProduct = filterCtx?.show_product_filter ?? (data.available_products?.length ?? 0) > 0
  const siteLabel = filterCtx?.site_label ?? 'View by site'
  const productLabel = filterCtx?.product_label ?? 'View by product'
  const isDrilldown = Boolean(data.selected_product || data.selected_site)
  const drillLabel = data.drilldown_label
    ?? data.selected_product
    ?? data.available_sites?.find((s) => s.code === data.selected_site)?.label
    ?? data.selected_site
  const grade = gradeFromScore(data.my_score)
  const topPerformer = data.team_members[0]
  const bottomPerformer = data.team_members[data.team_members.length - 1]
  const teamAvg = data.team_members.length
    ? Math.round(data.team_members.reduce((s, m) => s + m.avg_score, 0) / data.team_members.length)
    : data.my_score
  const products = data.available_products ?? []
  const sites = data.available_sites ?? []
  const outcomes = data.product_outcomes

  return (
    <motion.div className='space-y-6'>

      {(showSite || showProduct) && (
        <Card className='p-4'>
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className='flex flex-col gap-4'
          >
            {showSite && (
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-2'>
                  <Icons.building className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
                  <span className='text-sm font-medium text-brand-navy'>{siteLabel}</span>
                </div>
                <Select value={selectedSite} onValueChange={setSelectedSite}>
                  <SelectTrigger className='w-full sm:w-64 bg-white text-brand-navy'>
                    <SelectValue placeholder='All sites' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SITES}>All sites (overview)</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {s.label}
                        {s.call_count > 0 ? ` · ${s.call_count} calls` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {showProduct && (
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-2'>
                  <Icons.layers className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
                  <span className='text-sm font-medium text-brand-navy'>{productLabel}</span>
                </div>
                <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                  <SelectTrigger className='w-full sm:w-64 bg-white text-brand-navy'>
                    <SelectValue placeholder='All products' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PRODUCTS}>All products (overview)</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.code} value={p.code}>
                        {p.label}
                        {p.call_count > 0 ? ` · ${p.call_count} calls` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </motion.div>
        </Card>
      )}

      {showProduct && products.length === 0 && (
        <p className='text-xs text-amber-600'>
          No products found in team mapping. Import team Excel with a Product column, then refresh.
        </p>
      )}
      {showSite && sites.length === 0 && (
        <p className='text-xs text-amber-600'>
          No sites found in team mapping. Import team Excel with Site columns, then refresh.
        </p>
      )}

      {isDrilldown && drillLabel && (
        <p className='text-xs text-muted-foreground'>
          Filtered view for <span className='font-medium text-brand-navy'>{drillLabel}</span>
          {data.employee_count != null ? ` · ${data.employee_count} mapped employees` : ''}
          {' '}— aggregated metrics only, not individual calls.
        </p>
      )}

      <motion.div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        <KpiStatCard
          title={isDrilldown ? 'Filtered Avg Score' : 'My KPI Score'}
          value={Math.round(data.my_score)}
          suffix='%'
          icon={Icons.trophy}
          colorClass='bg-brand-navy'
          delay={0}
          sub={isDrilldown
            ? `Grade ${grade.label} · ${drillLabel}`
            : `Grade ${grade.label} · ${ROLE_LABELS[data.my_role] ?? data.my_role}`}
        />
        <KpiStatCard
          title={isDrilldown ? 'Agents in scope' : 'Team Avg Score'}
          value={isDrilldown ? data.team_members.length : teamAvg}
          suffix={isDrilldown ? '' : '%'}
          icon={Icons.users}
          colorClass='bg-brand-cornflower'
          delay={1}
          sub={isDrilldown
            ? `${data.employee_count ?? 0} employees mapped`
            : `${data.team_size ?? data.team_members.length} members`}
        />
        <KpiStatCard
          title='Total Calls'
          value={data.call_count}
          icon={Icons.phone}
          colorClass='bg-emerald-600'
          delay={2}
          sub={isDrilldown ? `For ${drillLabel}` : 'Within date range'}
        />
        <KpiStatCard
          title='Fatal Issues'
          value={data.fatal_count}
          icon={Icons.alertTriangle}
          colorClass={data.fatal_count > 0 ? 'bg-red-600' : 'bg-slate-600'}
          delay={3}
          sub={data.fatal_count === 0 ? 'All clear' : 'Policy violations'}
        />
      </motion.div>

      {isDrilldown && outcomes && (
        <motion.div className='grid grid-cols-3 gap-4'>
          <KpiStatCard
            title='Good outcomes'
            value={outcomes.good}
            icon={Icons.checkCircle}
            colorClass='bg-emerald-600'
            delay={0}
            sub='Score 70+ or positive'
          />
          <KpiStatCard
            title='Neutral'
            value={outcomes.neutral}
            icon={Icons.minus}
            colorClass='bg-slate-600'
            delay={1}
            sub='Mid-range performance'
          />
          <KpiStatCard
            title='Needs improvement'
            value={outcomes.bad}
            icon={Icons.alertCircle}
            colorClass='bg-red-600'
            delay={2}
            sub='Score below 55 or negative'
          />
        </motion.div>
      )}

      {/* ── Row 2: Role Avg + Score Trend ── */}
      <div className='grid gap-6 lg:grid-cols-2'>

        {/* Role Level Comparison */}
        <motion.div custom={4} variants={cardVariants} initial='hidden' animate='visible'>
          <Card className='relative overflow-hidden h-full'>
            <CardWatermark opacity={2} />
            <CardHeader className='relative z-10 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                <Icons.barChart className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
                Score by Role Level
              </CardTitle>
              <p className='text-xs text-muted-foreground'>Average score across each hierarchy level</p>
            </CardHeader>
            <CardContent className='relative z-10'>
              {data.role_averages.length === 0
                ? <EmptyState label='No role data yet' />
                : (
                  <ResponsiveContainer width='100%' height={220}>
                    <BarChart data={data.role_averages} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke={CHART_GRID} />
                      <XAxis dataKey='role' tick={{ fill: CHART_TICK, fontSize: 11 }}
                        tickFormatter={(v) => ROLE_LABELS[v] ?? v} />
                      <YAxis domain={[0, 100]} tick={{ fill: CHART_TICK, fontSize: 11 }} />
                      <Tooltip content={<ScoreTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                      <ReferenceLine y={70} stroke='#3b82f6' strokeDasharray='4 4' strokeOpacity={0.5} />
                      <Bar dataKey='avg_score' name='Avg Score' radius={[6, 6, 0, 0]} maxBarSize={56}>
                        {data.role_averages.map((entry) => (
                          <Cell key={entry.role} fill={ROLE_COLORS[entry.role] ?? '#6366f1'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </CardContent>
          </Card>
        </motion.div>

        {/* Weekly Score Trend */}
        <motion.div custom={5} variants={cardVariants} initial='hidden' animate='visible'>
          <Card className='relative overflow-hidden h-full'>
            <CardWatermark opacity={2} />
            <CardHeader className='relative z-10 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                <Icons.trendingUp className='h-4 w-4 text-emerald-400' strokeWidth={1.5} />
                Weekly Score Trend
              </CardTitle>
              <p className='text-xs text-muted-foreground'>
                {isDrilldown ? 'Average filtered score per calendar week' : 'Average team score per calendar week'}
              </p>
            </CardHeader>
            <CardContent className='relative z-10'>
              {data.weekly_trend.length === 0
                ? <EmptyState label='No trend data for selected period' />
                : (
                  <ResponsiveContainer width='100%' height={220}>
                    <LineChart data={data.weekly_trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke={CHART_GRID} />
                      <XAxis dataKey='week' tick={{ fill: CHART_TICK, fontSize: 11 }}
                        tickFormatter={(v) => `Wk ${v}`} />
                      <YAxis domain={[0, 100]} tick={{ fill: CHART_TICK, fontSize: 11 }} />
                      <Tooltip content={<ScoreTooltip />} />
                      <ReferenceLine y={70} stroke='#3b82f6' strokeDasharray='4 4' strokeOpacity={0.5} label={{ value: 'Target', fill: '#94a3b8', fontSize: 10 }} />
                      <Line type='monotone' dataKey='avg_score' name='Avg Score' stroke='#10b981'
                        strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )
              }
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* ── Row 3: Pareto + Score Distribution ── */}
      <div className='grid gap-6 lg:grid-cols-2'>

        {/* Score Distribution Histogram */}
        <motion.div custom={7} variants={cardVariants} initial='hidden' animate='visible'>
          <Card className='relative overflow-hidden'>
            <CardWatermark opacity={2} />
            <CardHeader className='relative z-10 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                <Icons.activity className='h-4 w-4 text-blue-400' strokeWidth={1.5} />
                Score Distribution
              </CardTitle>
              <p className='text-xs text-muted-foreground'>
                {isDrilldown ? 'How call scores spread for this filter' : 'How scores spread across all team members'}
              </p>
            </CardHeader>
            <CardContent className='relative z-10'>
              {!data.score_distribution?.some(b => b.count > 0)
                ? <EmptyState label='No score data for selected period' />
                : (
                  <ResponsiveContainer width='100%' height={260}>
                    <BarChart data={data.score_distribution ?? []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke={CHART_GRID} />
                      <XAxis dataKey='range' tick={{ fill: CHART_TICK, fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fill: CHART_TICK, fontSize: 11 }} />
                      <Tooltip content={<DistributionTooltip />} />
                      <Bar dataKey='count' name='Members' radius={[4, 4, 0, 0]} maxBarSize={40}>
                        {(data.score_distribution ?? []).map((entry) => (
                          <Cell key={entry.range} fill={SCORE_BIN_COLOR(entry.range)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* ── Direct Report Scorecard ── */}
      {(data.direct_reports ?? []).length > 0 && (
        <motion.div custom={7.5} variants={cardVariants} initial='hidden' animate='visible'>
          <Card className='relative overflow-hidden'>
            <CardWatermark opacity={2} />
            <CardHeader className='relative z-10 pb-3'>
              <div className='flex items-center justify-between'>
                <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                  <Icons.users className='h-4 w-4 text-brand-purple' strokeWidth={1.5} />
                  {ROLE_LABELS[data.my_role] ?? data.my_role} — Direct Report Scores
                </CardTitle>
                <span className='text-[11px] text-muted-foreground'>
                  {(data.direct_reports ?? []).length} report{(data.direct_reports ?? []).length !== 1 ? 's' : ''}
                  &nbsp;· AI-scored avg
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>
                Average score per direct report — computed from AI-analysed calls
              </p>
            </CardHeader>
            <CardContent className='relative z-10 space-y-2.5'>
              {(data.direct_reports ?? []).map((m, idx) => {
                const g = gradeFromScore(m.avg_score)
                const pct = Math.min(m.avg_score, 100)
                return (
                  <div key={`${m.display_name}-${idx}`} className='flex items-center gap-3'>
                    <span className='w-5 text-right text-[11px] font-medium text-muted-foreground'>{idx + 1}</span>
                    <div className='flex w-40 min-w-0 flex-col'>
                      <span className='truncate text-xs font-medium text-brand-navy'>{m.display_name}</span>
                      <span className='text-[10px] text-muted-foreground'>{ROLE_LABELS[m.role] ?? m.role}</span>
                    </div>
                    <div className='flex-1'>
                      <div className='h-2 w-full overflow-hidden rounded-full bg-gray-100'>
                        <motion.div
                          className={cn('h-full rounded-full', {
                            'bg-emerald-500': m.avg_score >= 85,
                            'bg-blue-500':    m.avg_score >= 70 && m.avg_score < 85,
                            'bg-amber-500':   m.avg_score >= 55 && m.avg_score < 70,
                            'bg-red-500':     m.avg_score < 55,
                          })}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ delay: 0.1 + idx * 0.04, duration: 0.5 }}
                        />
                      </div>
                    </div>
                    <span className='w-12 text-right text-sm font-semibold text-brand-navy'>
                      {m.avg_score > 0 ? `${Math.round(m.avg_score)}%` : '—'}
                    </span>
                    <span className={cn('w-6 rounded px-1 text-center text-xs font-bold', g.bg, g.color)}>
                      {m.avg_score > 0 ? g.label : '—'}
                    </span>
                    <span className='w-16 text-right text-[10px] text-muted-foreground'>
                      {m.call_count} call{m.call_count !== 1 ? 's' : ''}
                    </span>
                    {m.fatal_count > 0 && (
                      <span className='flex items-center gap-0.5 text-[10px] text-red-400'>
                        <Icons.alertCircle className='h-3 w-3' /> {m.fatal_count}
                      </span>
                    )}
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Row 4: Team Leaderboard ── */}
      {data.team_members.length > 0 && (
        <motion.div custom={8} variants={cardVariants} initial='hidden' animate='visible'>
          <Card className='relative overflow-hidden'>
            <CardWatermark opacity={2} />
            <CardHeader className='relative z-10 pb-3'>
              <div className='flex items-center justify-between'>
                <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                  <Icons.users className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
                  {isDrilldown ? 'Agents in filter' : 'Team Leaderboard'}
                </CardTitle>
                <div className='flex items-center gap-3 text-[11px] text-muted-foreground'>
                  <span className='flex items-center gap-1'><span className='inline-block h-2 w-2 rounded-full bg-emerald-500' /> Grade A (85+)</span>
                  <span className='flex items-center gap-1'><span className='inline-block h-2 w-2 rounded-full bg-blue-500' /> B (70+)</span>
                  <span className='flex items-center gap-1'><span className='inline-block h-2 w-2 rounded-full bg-amber-500' /> C (55+)</span>
                  <span className='flex items-center gap-1'><span className='inline-block h-2 w-2 rounded-full bg-red-500' /> D (&lt;55)</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className='relative z-10 space-y-2.5'>
              {data.team_members.slice(0, 15).map((m, idx) => {
                const g = gradeFromScore(m.avg_score)
                const pct = Math.min(m.avg_score, 100)
                return (
                  <div key={m.user_id} className='flex items-center gap-3'>
                    <span className='w-5 text-right text-[11px] font-medium text-muted-foreground'>{idx + 1}</span>
                    <div className='flex w-36 min-w-0 flex-col'>
                      <span className='truncate text-xs font-medium text-brand-navy'>{m.display_name}</span>
                      <span className='text-[10px] text-muted-foreground'>{ROLE_LABELS[m.role] ?? m.role}</span>
                    </div>
                    <div className='flex-1'>
                      <div className='h-2 w-full overflow-hidden rounded-full bg-gray-100'>
                        <motion.div
                          className={cn('h-full rounded-full', {
                            'bg-emerald-500': m.avg_score >= 85,
                            'bg-blue-500': m.avg_score >= 70 && m.avg_score < 85,
                            'bg-amber-500': m.avg_score >= 55 && m.avg_score < 70,
                            'bg-red-500': m.avg_score < 55,
                          })}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ delay: 0.1 + idx * 0.03, duration: 0.5 }}
                        />
                      </div>
                    </div>
                    <span className='w-12 text-right text-sm font-semibold text-brand-navy'>{Math.round(m.avg_score)}%</span>
                    <span className={cn('w-6 rounded px-1 text-center text-xs font-bold', g.bg, g.color)}>{g.label}</span>
                    {m.fatal_count > 0 && (
                      <span className='flex items-center gap-0.5 text-[10px] text-red-400'>
                        <Icons.alertCircle className='h-3 w-3' /> {m.fatal_count}
                      </span>
                    )}
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Row 5: Top vs Bottom highlight ── */}
      {data.team_members.length >= 2 && (
        <div className='grid gap-4 sm:grid-cols-2'>
          <motion.div custom={9} variants={cardVariants} initial='hidden' animate='visible'>
            <Card className='relative overflow-hidden border-emerald-500/20'>
              <CardWatermark opacity={2} />
              <CardContent className='relative z-10 p-5'>
                <p className='mb-1 text-xs font-medium text-muted-foreground'>Top Performer</p>
                <p className='text-lg font-bold text-brand-navy'>{topPerformer?.display_name}</p>
                <p className='text-xs text-muted-foreground'>{ROLE_LABELS[topPerformer?.role] ?? topPerformer?.role}</p>
                <div className='mt-3 flex items-baseline gap-1'>
                  <span className='text-4xl font-black text-emerald-400'>{Math.round(topPerformer?.avg_score ?? 0)}</span>
                  <span className='text-lg text-brand-muted'>%</span>
                </div>
                <p className='mt-1 text-xs text-brand-muted'>{topPerformer?.call_count} calls analysed</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div custom={10} variants={cardVariants} initial='hidden' animate='visible'>
            <Card className='relative overflow-hidden border-red-500/20'>
              <CardWatermark opacity={2} />
              <CardContent className='relative z-10 p-5'>
                <p className='mb-1 text-xs font-medium text-muted-foreground'>Needs Coaching</p>
                <p className='text-lg font-bold text-brand-navy'>{bottomPerformer?.display_name}</p>
                <p className='text-xs text-muted-foreground'>{ROLE_LABELS[bottomPerformer?.role] ?? bottomPerformer?.role}</p>
                <div className='mt-3 flex items-baseline gap-1'>
                  <span className='text-4xl font-black text-red-400'>{Math.round(bottomPerformer?.avg_score ?? 0)}</span>
                  <span className='text-lg text-brand-muted'>%</span>
                </div>
                <p className='mt-1 text-xs text-brand-muted'>{bottomPerformer?.fatal_count} fatal issues</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

    </motion.div>
  )
}
